import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { LegacyShell } from "@/components/PageShell";
import { PRODUCTS } from "@/data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/how-it-went`;
const CHOICES = [["plenty", "We've noticed a difference", "Nous avons remarqué une différence"], ["some", "A little, perhaps", "Un peu, peut-être"], ["not_yet", "Not yet", "Pas encore"]];

const JarQuestion = ({ product, a, set, t }) => (
  <fieldset className="border border-[#303B2D]/70 rounded-sm p-6" data-testid={`hiw-jar-${product.id}`}>
    <legend className="px-2 font-display text-2xl text-[#E8DDC7]">{product.name}</legend>
    <div className="flex flex-wrap gap-2">
      {CHOICES.map(([k, en, fr]) => (
        <button type="button" key={k} onClick={() => set({ noticed: k })} aria-pressed={a.noticed === k} data-testid={`hiw-${product.id}-${k}`}
          className={`px-4 py-2.5 text-[13px] rounded-sm border transition-colors duration-300 ${a.noticed === k ? "border-[#B5A642] bg-[#B5A642]/10 text-[#E8DDC7]" : "border-[#303B2D] text-[#D3CEC4]/70 hover:border-[#B5A642]/50"}`}>
          {t(en, fr)}
        </button>
      ))}
    </div>
    <textarea rows={3} maxLength={600} value={a.note} onChange={(e) => set({ note: e.target.value })} data-testid={`hiw-${product.id}-note`}
      placeholder={t("Anything you'd like to tell us? (optional)", "Quelque chose à nous dire? (facultatif)")}
      className="mt-4 w-full bg-transparent border border-[#303B2D] rounded-sm px-3 py-2.5 text-[15px] text-[#E8DDC7] focus:outline-none focus:border-[#B5A642]/70 transition-colors" />
    {a.note.trim() && (
      <label className="mt-3 flex items-start gap-3 text-[13px] text-[#D3CEC4]/70 cursor-pointer">
        <input type="checkbox" checked={a.share} onChange={(e) => set({ share: e.target.checked })} data-testid={`hiw-${product.id}-share`} className="mt-0.5 accent-[#B5A642]" />
        {t("You may share this on the jar's page with my first name and province.", "Vous pouvez partager ceci sur la page du pot avec mon prénom et ma province.")}
      </label>
    )}
  </fieldset>
);

export default function HowItWent() {
  const { token } = useParams();
  const { t } = useLang();
  const [c, setC] = useState(null);
  const [answers, setAnswers] = useState({});
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/${token}`).then((r) => {
      setC(r.data);
      setAnswers(Object.fromEntries((r.data?.products || []).map((p) => [p, { noticed: "", note: "", share: false }])));
    }).catch(() => setC(false));
  }, [token]);

  const products = (c?.products || []).map((id) => PRODUCTS.find((p) => p.id === id)).filter(Boolean);
  const ready = products.length > 0 && products.every((p) => answers[p.id]?.noticed);
  const submit = async (e) => {
    e.preventDefault();
    setState("sending"); setError("");
    try {
      await axios.post(`${API}/${token}`, { answers: products.map((p) => ({ product_id: p.id, ...answers[p.id] })) });
      setState("done");
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : t("That didn't send. Please try again.", "L'envoi a échoué. Veuillez réessayer."));
      setState("idle");
    }
  };

  return (
    <LegacyShell testid="how-it-went-page" noindex>
      <main className="max-w-2xl mx-auto px-5 md:px-10 py-20" data-no-translate>
        <p className="font-display italic text-[#B5A642]">{t("A month in", "Un mois plus tard")}</p>
        {c === null && <p className="mt-6 text-[#D3CEC4]/50">…</p>}
        {c === false && <h1 className="mt-3 font-display font-light text-4xl text-[#E8DDC7]" data-testid="hiw-missing">{t("We couldn't find that check-in.", "Nous n'avons pas trouvé ce suivi.")}</h1>}
        {c && (c.answered || state === "done") && (
          <div data-testid="hiw-thanks">
            <h1 className="mt-3 font-display font-light text-4xl sm:text-5xl text-[#E8DDC7]">{t("Thank you. It's in the ledger.", "Merci. C'est au registre.")}</h1>
            <p className="mt-5 text-[15px] leading-relaxed text-[#D3CEC4]/70">{t("Every answer is read by a person. The dogs have been informed.", "Chaque réponse est lue par une personne. Les chiens ont été informés.")}</p>
            <Link to="/shop" className="mt-8 inline-block text-[11px] uppercase tracking-[0.2em] text-[#B5A642]">{t("Back to the shop", "Retour à la boutique")}</Link>
          </div>
        )}
        {c && !c.answered && state !== "done" && (
          <form onSubmit={submit} className="space-y-6">
            <h1 className="mt-3 font-display font-light text-4xl sm:text-5xl text-[#E8DDC7]">{t(`How did it go, ${c.first_name}?`, `Comment ça s'est passé, ${c.first_name}?`)}</h1>
            <p className="text-[15px] leading-relaxed text-[#D3CEC4]/70">{t("One tap per jar. A line or two if you have it in you. Honest answers, including 'not yet', help us most.", "Un clic par pot. Une ligne ou deux si le cœur vous en dit. Les réponses honnêtes, y compris « pas encore », nous aident le plus.")}</p>
            {products.map((p) => (
              <JarQuestion key={p.id} product={p} a={answers[p.id]} t={t}
                set={(patch) => setAnswers((x) => ({ ...x, [p.id]: { ...x[p.id], ...patch } }))} />
            ))}
            {error && <p className="text-[13px] text-[#C9876B]" data-testid="hiw-error">{error}</p>}
            <button type="submit" disabled={!ready || state === "sending"} data-testid="hiw-submit"
              className="px-7 py-3.5 bg-[#4B3226] text-[#E8DDC7] text-[12px] uppercase tracking-[0.2em] rounded-sm hover:brightness-125 disabled:opacity-40 transition-[filter,opacity] duration-300">
              {state === "sending" ? t("Sending…", "Envoi…") : t("Send it to the house", "Envoyer à la maison")}
            </button>
          </form>
        )}
      </main>
    </LegacyShell>
  );
}
